import is from '@sindresorhus/is';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
    RealtimeEventListener,
    RealtimeEventListenerBuilder,
} from '../realtime-event-listener/realtime-event-listener';
import type { AudioRecorder } from './audio-recorder';
import { createAudioRecorder } from './audio-recorder';

const AUDIO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'wav', 'mpeg', 'mp3', 'aac'] as const;

const getAudioFileExtension = (mimeType: string): string =>
    AUDIO_EXTENSIONS.find((ext) => mimeType.includes(ext)) ?? 'audio';

export type AudioTranscriptionStatus = 'error' | 'idle' | 'recording' | 'transcribing';

export type UseAudioTranscriptionOptions = {
    readonly onChunk: (text: string) => void;
    readonly onComplete: () => void;
    readonly onError: () => void;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
};

export type UseAudioTranscriptionReturn = {
    readonly error: string | null;
    readonly isRecording: boolean;
    readonly isTranscribing: boolean;
    readonly startRecording: () => Promise<void>;
    readonly status: AudioTranscriptionStatus;
    readonly stopRecording: () => Promise<void>;
};

export const useAudioTranscription = ({
    onChunk,
    onComplete,
    onError,
    realtimeEventListenerBuilder,
}: UseAudioTranscriptionOptions): UseAudioTranscriptionReturn => {
    const [status, setStatus] = useState<AudioTranscriptionStatus>('idle');
    const [error, setError] = useState<string | null>(null);

    const audioRecorderRef = useRef<AudioRecorder | null>(null);
    const listenerRef = useRef<RealtimeEventListener | null>(null);
    const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (completionTimeoutRef.current !== null) {
                clearTimeout(completionTimeoutRef.current);
                completionTimeoutRef.current = null;
            }
        };
    }, []);

    const startRecording = useCallback(async () => {
        setError(null);

        const audioRecorder = createAudioRecorder({
            createMediaRecorder: (stream) =>
                new globalThis.MediaRecorder(stream, {
                    audioBitsPerSecond: 128_000,
                }),
            getUserMedia: (mediaConstraints) => navigator.mediaDevices.getUserMedia(mediaConstraints),
        });

        await audioRecorder.start();

        if (!is.nullOrUndefined(audioRecorder.error)) {
            setError(audioRecorder.error);
            setStatus('error');

            return;
        }

        audioRecorderRef.current = audioRecorder;
        setStatus('recording');
    }, []);

    const stopRecording = useCallback(async () => {
        const audioRecorder = audioRecorderRef.current;

        if (is.nullOrUndefined(audioRecorder)) {
            return;
        }

        setStatus('transcribing');

        const audioBlob = await audioRecorder.stop();
        audioRecorderRef.current = null;

        if (listenerRef.current !== null) {
            listenerRef.current.stop();
        }

        realtimeEventListenerBuilder.requestHeaders = {};
        const formData = new FormData();
        const extension = getAudioFileExtension(audioBlob.type);
        formData.append('audio', audioBlob, `audio.${extension}`);

        const sse = realtimeEventListenerBuilder.build({
            autoReconnect: false,
            path: '/v1/transcribe/audio/stream',
            payload: formData,
        });

        listenerRef.current = sse;

        let hasReceivedData = false;

        const handleComplete = (): void => {
            if (completionTimeoutRef.current !== null) {
                clearTimeout(completionTimeoutRef.current);
                completionTimeoutRef.current = null;
            }
            setStatus('idle');
            listenerRef.current = null;
            onComplete();
        };

        const handleError = (): void => {
            if (completionTimeoutRef.current !== null) {
                clearTimeout(completionTimeoutRef.current);
                completionTimeoutRef.current = null;
            }
            if (hasReceivedData) {
                setStatus('idle');
                listenerRef.current = null;
                onComplete();
            } else {
                setStatus('error');
                setError('Transcription failed');
                listenerRef.current = null;
                onError();
            }
        };

        const scheduleCompletion = (): void => {
            if (completionTimeoutRef.current !== null) {
                clearTimeout(completionTimeoutRef.current);
            }
            completionTimeoutRef.current = setTimeout(() => {
                if (hasReceivedData) {
                    handleComplete();
                }
                completionTimeoutRef.current = null;
            }, 500);
        };

        sse.addEventListener('chunk', (e: MessageEvent) => {
            hasReceivedData = true;
            onChunk(e.data as string);
            scheduleCompletion();
        });

        sse.addEventListener('complete', handleComplete);
        sse.addEventListener('done', handleComplete);
        sse.addEventListener('end', handleComplete);
        sse.addEventListener('abort', handleComplete);

        sse.addEventListener('close', handleError);

        sse.start();
    }, [onChunk, onComplete, onError, realtimeEventListenerBuilder]);

    return {
        error,
        isRecording: status === 'recording',
        isTranscribing: status === 'transcribing',
        startRecording,
        status,
        stopRecording,
    };
};
